import { twJoin } from "tailwind-merge";
import { NodeSelector } from "~/js/graph/nodeSelector";
import { UndoManager } from "~/js/graph/undoManager";
import { deserializeReroutingNodeAdding } from "~/js/utilities/deserializeReroutingNodeAdding";
import {
	calculateBoundariesOfNode,
	calculateBoundariesOfObjects,
	fitEditorToBoundary
} from "~/js/utilities/fitToScreenUtils";
import { generate_UUID } from "~/js/utilities/UUID";
import { loadWorkspace, updateNodeWithFiles } from "~/js/workspace";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { WorkspaceApi } from "~/solidJs/entities/WorkspaceListEntity/api/WorkspaceApi";
import { setWorkspacesStore } from "~/solidJs/entities/WorkspaceListEntity/models/wrokspaceListContext";
import {
	DebugFlags,
	handlePasteImageEvent,
	isDebugFlag,
	sessionCounter,
	TextAreaSizeObserver
} from "~/solidJs/shared/helpers";
import {
	copiedNodesToClipBoardFormats,
	readClipboardData,
	updateNodePositionsForCentering
} from "~/solidJs/shared/helpers/cllipBoardHelpers";
import { addLocalNodeByGlobalId, isNodeDisabled } from "~/solidJs/shared/helpers/nodeHelpers";
import {
	getSharedOrUserWorkspace,
	getTemplateFromLink
} from "~/solidJs/shared/helpers/workspaceHelpers";
import { isLimitOffer } from "~/solidJs/widgets/DiscountOfferModal/DiscountOfferModal";
import {
	CommentFrame,
	CommentFrameSticker,
	loadCommentFrameAccordType
} from "../nodes/local/logic/commentFrame";
import { SocketDefinition } from "../nodes/node-definition";
import { NodeEvents } from "../types/IEventorListners";
import EventAnalytics, { setSelectedTemplateId } from "../utilities/EventAnalytics";
import Eventor from "../utilities/Eventor";
import Navigation from "./navigation";
import { getDefinitionOrAwait } from "./node-library";
import { Connection, GraphNode } from "./node-socket-connection";

export default class Graph {
	NODES_WARNING_AMOUNT = 20;
	WARNING_SHOWN = false;
	isSaved = true;
	nodesMoved = false;
	/**
	 * prevents executing the node when moveing nodes
	 */
	nodesMovedExecutor = false;
	isLastNodeWasMoved = false;
	lastNodeOffset = 0;
	firstNodeWasAdded = false;
	isFocused = true;
	/**
	 * @type {GraphNode[]}
	 */
	nodes = [];
	/**
	 * @type {boolean}
	 */
	isStylesWereOppen = false;
	/**
	 * @type {Map<string, GraphNode>}
	 */
	nodesHashMap = new Map();
	/**
	 * @type {Map<string|number, CommentFrame>}
	 */
	commentFrameMap = new Map();
	/**
	 * @type {Map<string|number, CommentFrame>}
	 */
	selectedCommentFrameMap = new Map();
	selectedNodes = [];
	selectedNodesMap = new Map();
	copiedNodesDefinitions = [];
	copiedCommentFrames = [];
	/**
	 * @description is user reach generation limits disability
	 * @type {boolean}
	 */
	limitDisabled;
	/**
	 * @description can user run disabled nodes
	 * @type {boolean}
	 */
	canRunDisabled;
	eventor = new Eventor();
	textAreasObserver = new TextAreaSizeObserver();
	/**
	 * @type {{graph: HTMLElement, editorSpace: HTMLElement}}
	 */
	dom;
	constructor(editorSpaceElement, graphElement, isNeedToInitState = true) {
		this.dom = {};
		this.dom.graph = graphElement;
		this.dom.editorSpace = editorSpaceElement;
		this.navigation = new Navigation(editorSpaceElement, graphElement);
		this.nodeSelector = new NodeSelector();
		this.domElement().append(this.nodeSelector.domElement());

		// Event handlers storage for proper cleanup
		this.eventHandlers = {
			editorSpaceMouseDown: null,
			dragOver: null,
			dragLeave: null,
			bodyDrop: null,
			editorSpaceDrop: null,
			paste: null,
			copy: null,
			pointerDown: null,
			bodyPointerDown: null,
			keyDown: null
		};

		this.enableStartTips();

		// Store and add event listeners with proper cleanup references
		this.eventHandlers.editorSpaceMouseDown = (event) => {
			if (event.shiftKey) return;

			this.deselectAllNodes();
			this.nodeSelector.mouseDownHandler(event);
		};
		editorSpaceElement.addEventListener("mousedown", this.eventHandlers.editorSpaceMouseDown);
		this.initNodeActions();
		this.initDragAndDropFeature();
		this.alreadyOpenedNodeIds = {};
		const user = getUserStore();
		this.limitDisabled = false;
		this.canRunDisabled = user.can_run_disabled;
		this.revalidateDisabilityFromData(user);
		this.foucsHandler = () => this.revalidateDisability();
		window.addEventListener("focus", this.foucsHandler);
		if (isNeedToInitState) {
			setTimeout(() => this.initState(), 0);
		}
		this.undoManager = new UndoManager();
		this.initOptimizationListner();
	}

	incrimentLastNodeOffset() {
		const prevOffset = this.lastNodeOffset;
		this.lastNodeOffset += 50;
		return prevOffset;
	}

	toggleLastMove() {
		if (this.isLastNodeWasMoved) {
			this.isLastNodeWasMoved = false;
			this.lastNodeOffset = 0;
		}
	}

	destructor() {
		// Remove window focus listener
		window.removeEventListener("focus", this.foucsHandler);

		// Disconnect text areas observer
		this.textAreasObserver.disconnect();

		// Clear node selector
		if (this.nodeSelector) {
			this.nodeSelector = null;
		}

		// Clear eventor
		if (this.eventor) {
			this.eventor = null;
		}
		if (!this.eventHandlers) return;
		// Remove all stored event listeners
		if (this.eventHandlers.editorSpaceMouseDown) {
			this.dom.editorSpace.removeEventListener(
				"mousedown",
				this.eventHandlers.editorSpaceMouseDown
			);
		}

		if (this.eventHandlers.dragOver) {
			this.dom.editorSpace.removeEventListener("dragover", this.eventHandlers.dragOver);
		}

		if (this.eventHandlers.dragLeave) {
			this.dom.editorSpace.removeEventListener("dragleave", this.eventHandlers.dragLeave);
		}

		if (this.eventHandlers.bodyDrop) {
			document.body.removeEventListener("drop", this.eventHandlers.bodyDrop, true);
		}

		if (this.eventHandlers.editorSpaceDrop) {
			this.dom.editorSpace.removeEventListener("drop", this.eventHandlers.editorSpaceDrop);
		}

		if (this.eventHandlers.paste) {
			document.removeEventListener("paste", this.eventHandlers.paste);
		}

		if (this.eventHandlers.copy) {
			document.removeEventListener("copy", this.eventHandlers.copy);
		}

		if (this.eventHandlers.pointerDown) {
			this.dom.editorSpace.removeEventListener("pointerdown", this.eventHandlers.pointerDown);
		}

		if (this.eventHandlers.bodyPointerDown) {
			document.body.removeEventListener("pointerdown", this.eventHandlers.bodyPointerDown);
		}

		if (this.eventHandlers.keyDown) {
			document.removeEventListener("keydown", this.eventHandlers.keyDown);
		}
		// Clear the eventHandlers object
		this.eventHandlers = null;
	}
	get getIsSaved() {
		return this.isSaved;
	}

	async initState() {
		if (isDebugFlag(DebugFlags.DisableInitTemplate)) {
			return;
		}
		let isAdded = await getSharedOrUserWorkspace(window.location.pathname);
		if (isAdded) return;
		isAdded = await getTemplateFromLink(window.location.pathname);
		if (isAdded) return;
		const curSession = sessionCounter.getCurSessionCount();
		if (curSession === 1 || isDebugFlag(DebugFlags.OnboardingDebug)) {
			this.loadInitWorkspace();
			return;
		}

		const urlParams = new URLSearchParams(window.location.search);
		const nodeToAdd = urlParams.get("nodeToAdd");
		if (nodeToAdd) {
			deserializeReroutingNodeAdding();
			return;
		}
	}

	async loadInitWorkspace() {
		const selectedId = 445;
		EventAnalytics.loadInitTemplate(selectedId);
		setSelectedTemplateId(selectedId);
		const workspaceConfig = await WorkspaceApi.getOnboardingTemplate(selectedId);
		if (!workspaceConfig) return;
		try {
			await loadWorkspace(workspaceConfig.WorkspaceTemplate.workspace);
		} catch {}
		fitEditorToBoundary({
			...calculateBoundariesOfObjects(this.nodes, [...this.commentFrameMap.values()]),
			scalePadding: 1.6
		});
	}
	toggleIsSavedState(newValue) {
		setWorkspacesStore((prev) => ({ ...prev, isStateSaved: newValue }));
		this.isSaved = newValue;
	}
	toggleIsOpenPending(newValue) {
		setWorkspacesStore((prev) => {
			return { ...prev, isOpenPending: newValue };
		});
		this.isOpenPending = newValue;
	}
	selectNodesInBound({ left: startX, right: endX, top: startY, bottom: endY } = props) {
		for (let i = 0; i < this.nodes.length; i++) {
			const { left, right, bottom, top } = this.nodes[i].layout
				.domElement()
				.getBoundingClientRect();

			if (left < endX && right > startX && top < endY && bottom > startY) {
				this.addNodeToSelected(this.nodes[i]);
			}
		}
	}
	selectCommentFrameInBound({ left: startX, right: endX, top: startY, bottom: endY } = props) {
		for (const [_id, commentFrame] of this.commentFrameMap.entries()) {
			const { left, right, bottom, top } = commentFrame.domElement().getBoundingClientRect();
			if (left < endX && right > startX && top < endY && bottom > startY) {
				this.addCommentFrameToSelect(commentFrame);
				continue;
			}
			this.removeCommentFrameFromSelect(commentFrame);
		}
	}
	/**
	 *
	 * @param {*} isLimited
	 */
	toggleLimitDisabled(isLimited) {
		const prevLimit = this.limitDisabled;
		this.limitDisabled = isLimited;
		if (isLimited !== prevLimit) {
			if (isLimited) {
				isLimitOffer();
			}
			for (const node of this.nodes) {
				node.toggleLimitDisability(isLimited);
			}
		}
	}
	/**
	 *
	 * @param {*} isLimited
	 */
	toggleSidebarLimitDisabled(canRun) {
		this.canRunDisabled = canRun;
	}
	async revalidateDisability() {
		this.revalidateDisabilityFromData(getUserStore());
	}
	/**
	 *
	 * @param {IUserGenerationLimits} data
	 */
	revalidateDisabilityFromData(data) {
		if (!data) {
			this.toggleLimitDisabled(true);
			return;
		}
		const { become_available_at } = data;
		this.toggleLimitDisabled(!!become_available_at);
	}

	initDragAndDropFeature() {
		// Drag over handler
		this.eventHandlers.dragOver = (e) => {
			if (!Array.from(e.dataTransfer.types).includes("Files")) return;
			e.preventDefault();
			this.dom.editorSpace.classList.add("dragging");
		};
		this.dom.editorSpace.addEventListener("dragover", this.eventHandlers.dragOver);

		// Drag leave handler
		this.eventHandlers.dragLeave = (e) => {
			e.preventDefault();
			this.dom.editorSpace.classList.remove("dragging");
		};
		this.dom.editorSpace.addEventListener("dragleave", this.eventHandlers.dragLeave);

		// Body drop handler (prevent default)
		this.eventHandlers.bodyDrop = (e) => {
			e.preventDefault();
		};
		document.body.addEventListener("drop", this.eventHandlers.bodyDrop, true);

		// Editor space drop handler
		this.eventHandlers.editorSpaceDrop = (e) => {
			e.preventDefault();
			this.dom.editorSpace.classList.remove("dragging");
			if (!Array.from(e.dataTransfer.types).includes("Files")) return;
			const droppedFiles = Array.from(e.dataTransfer.files);

			const { x, y } = this.navigation.clientToGraphSpace(e.clientX, e.clientY);

			addLocalNodeByGlobalId({
				globalId: "Import Files",
				newOutputsFiles: droppedFiles,
				position: {
					x,
					y
				}
			});
		};
		this.dom.editorSpace.addEventListener("drop", this.eventHandlers.editorSpaceDrop);
	}

	domElement() {
		return this.dom.graph;
	}
	/**
	 * @description adds node to graph
	 * @param {NodeDefinition} data
	 */
	addNode(
		data,
		x,
		y,
		defaultParams = true,
		selectSingle = true,
		undoManagerIgnored = false,
		toggleIsSavedStateIgnored = false
	) {
		if (!this.firstNodeWasAdded) {
			this.firstNodeWasAdded = true;
			this.disableStartTips();
		}
		const node = new GraphNode(
			data,
			x,
			y,
			defaultParams,
			this.limitDisabled || isNodeDisabled(data)
		);
		this.dom.editorSpace.focus();
		this.toggleFocus(true);
		if (!toggleIsSavedStateIgnored) {
			this.toggleIsSavedState(false);
		}

		this.nodes.push(node);
		this.nodesHashMap.set(node.uuid, node);

		if (this.nodes.length > this.NODES_WARNING_AMOUNT && !this.WARNING_SHOWN) {
			// this.WARNING_SHOWN = true;
			// setIsToManyNodesVisible(true);
		}

		this.domElement().append(node.layout.domElement());

		if (selectSingle) {
			this.singleSelectNode(node);
		} else {
			this.addNodeToSelected(node);
		}

		node.layout.domElement().addEventListener("mousedown", (event) => {
			this.nodesMovedExecutor = false;
			event.stopPropagation();

			const BORDER_SIZE = 6;
			if (event.offsetX >= node.layout.domElement().offsetWidth - BORDER_SIZE) return;
			const mouseUpHandler = () => {
				document.removeEventListener("mouseup", mouseUpHandler);

				if (this.nodesMoved) {
					this.nodesMoved = false;
					return;
				}

				this.handleNodeSelection(node, event);
			};

			const nodeIsSelected = this.selectedNodes.includes(node);
			if (nodeIsSelected) {
				this.moveAllSelectedNodes(event);
				document.addEventListener("mouseup", mouseUpHandler);
			} else {
				this.handleNodeSelection(node, event);
				if (!event.shiftKey) {
					this.moveAllSelectedNodes(event);
					document.addEventListener("mouseup", mouseUpHandler);
				}
			}
		});

		this.setResizeAbility(node);
		if (!undoManagerIgnored) {
			setTimeout(async () => {
				const serializedState = await this.serializeNodesAndThierConnections(
					[node],
					false,
					true
				);
				!undoManagerIgnored &&
					this.undoManager.addNodeToManager(node.uuid, serializedState[0]);
			}, 0);
		}
		node.eventor.addListener(NodeEvents.OutputsChange, () => this.toggleIsSavedState(false));
		node.eventor.addListener(NodeEvents.ParamsChange, () => this.toggleIsSavedState(false));
		this.eventor.invoke("addNode", node.uuid);
		return node;
	}

	setResizeAbility(node) {
		const BORDER_SIZE = 6;
		const MIN_WIDTH = 160;
		const MAX_WIDTH = 540;
		const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

		let startWidth;
		let startMouseX;
		const resize = (e) => {
			const dx = startMouseX - e.x;
			node.layout.setWidth(clamp(startWidth - dx, MIN_WIDTH, MAX_WIDTH));
		};

		node.layout.domElement().addEventListener(
			"mousedown",
			(event) => {
				if (!event.target.classList.contains("node")) return false;

				startWidth = node.layout.domElement().offsetWidth;
				if (event.offsetX >= startWidth - BORDER_SIZE) {
					startMouseX = event.x;
					document.addEventListener("mousemove", resize, false);

					const mouseUp = () => {
						document.removeEventListener("mousemove", resize, false);
						document.removeEventListener("mouseup", mouseUp, false);
					};
					document.addEventListener("mouseup", mouseUp);
				}
			},
			false
		);
	}

	deleteNodeById(id) {
		this.deleteNode(this.nodesHashMap.get(id), false, false, true);
	}

	/**
	 * returns true if there is at least one node subscribed to the same task as the given node
	 * @param {GraphNode} node
	 */
	isSomeNodeSubscribedToSameTask(node) {
		return this.nodes.some(
			(n) => n !== node && n.taskID === node.taskID && n?.executuionBlock?.isExecuting
		);
	}

	async deleteNode(node, avoidAnalytics = false, multiRemove = false, undoIgnored = false) {
		!undoIgnored &&
			!multiRemove &&
			this.undoManager.deleteNodeFromManager(
				await this.serializeNodesAndThierConnections([node], false, false)
			);
		this.eventor.invoke("deleteNode", node.uuid);
		if (!this.isSomeNodeSubscribedToSameTask(node)) {
			node?.executuionBlock?.cancelTask?.();
		}
		const sockets = node.getAllSockets();
		for (const socket of sockets) {
			while (socket.connections.length > 0) {
				socket.connections[0].delete(false);
			}
		}
		node.remove();
		node.layout.domElement().remove();

		this.removeNodeFromSelected(node);

		const index = this.nodes.indexOf(node);
		if (index > -1) {
			this.nodes.splice(index, 1);
			this.nodesHashMap.delete(node.uuid);
		}
		if (!avoidAnalytics) {
			EventAnalytics.deleteNode(node.globalId);
		}

		this.toggleIsSavedState(this.nodes.length === 0);
	}

	/**
	 * @param {CommentFrameWorkspaceDefinition} commentFrameDefinition
	 */
	addCommentFrame(commentFrameDefinition) {
		commentFrameDefinition.id = generate_UUID();
		const id = commentFrameDefinition.id;
		const { url, x, y, width, height, ...rest } = commentFrameDefinition;
		let initialPosition;
		if (x !== undefined && y !== undefined) {
			initialPosition = {
				x,
				y
			};
		}
		const sticker = new CommentFrame({
			id,
			initialPosition,
			initialSize: {
				height,
				width
			},
			initialImageUrl: url,
			...rest
		});

		this.commentFrameMap.set(sticker.id, sticker);
		this.domElement().append(sticker.domElement());
		return sticker;
	}
	/**
	 * @param {CommentFrameWorkspaceDefinition} commentFrameDefinition
	 */
	addCommentFrameSticker(commentFrameDefinition) {
		commentFrameDefinition.id = generate_UUID();
		const id = commentFrameDefinition.id;
		const { x, y, width, height, ...rest } = commentFrameDefinition;
		let initialPosition;
		if (x !== undefined && y !== undefined) {
			initialPosition = {
				x,
				y
			};
		}
		const sticker = new CommentFrameSticker({
			id,
			initialPosition,
			initialSize: {
				height,
				width
			},
			...rest
		});

		this.commentFrameMap.set(sticker.id, sticker);
		this.domElement().append(sticker.domElement());
		return sticker;
	}

	/**
	 * @description removes comment frame from all data structures
	 * @param {{id: number|string}} entries
	 */
	deleteCommentFrame(entries) {
		const { id } = entries;
		this.commentFrameMap.get(id)?.removeFromDom();
		this.commentFrameMap.delete(id);
		this.selectedCommentFrameMap.delete(id);
	}

	/**
	 * @description add node to selected data structure
	 * @param {GraphNode} node
	 */
	addNodeToSelected(node) {
		if (node.selected) return;
		node.setSelect(true);
		this.selectedNodes.push(node);
		this.selectedNodesMap.set(node.uuid, true);
	}

	/**
	 * @description adds commentFrame to selection data structure
	 * @param {CommentFrame} commentFrame
	 */
	addCommentFrameToSelect(commentFrame) {
		commentFrame.select(true);
		this.selectedCommentFrameMap.set(commentFrame.id, commentFrame);
	}

	/**
	 * @description adds commentFrame to selection data structure
	 * @param {CommentFrame} commentFrame
	 */
	singleSelectCommentFrame(commentFrame) {
		this.deselectAllNodes();
		this.deselectAllCommetFrames();
		commentFrame.select(true);
		this.selectedCommentFrameMap.set(commentFrame.id, commentFrame);
	}

	/**
	 * @description adds commentFrame to selection data structure
	 * @param {CommentFrame} commentFrame
	 */
	deselectAllCommetFrames() {
		for (const frame of this.selectedCommentFrameMap.values()) {
			frame.select(false);
		}
		this.selectedCommentFrameMap.clear();
	}

	/**
	 * @description adds commentFrame to selection data structure
	 * @param {CommentFrame} commentFrame
	 */
	isCommentFrameSelected(commentFrame) {
		return this.selectedCommentFrameMap.has(commentFrame.id);
	}
	/**
	 * @description remove commentFrame from selection data structure
	 * @param {CommentFrame} commentFrame
	 */
	removeCommentFrameFromSelect(commentFrame) {
		commentFrame.select(false);
		this.selectedCommentFrameMap.delete(commentFrame.id);
	}
	/**
	 * serialize node state for in-memory copying (without sync files with server)
	 * @param {import("./node-socket-connection").Node} node - node to copy
	 * @param {boolean} isNeedTonewUUids - when we copy one instance of node multiple times, we need to generate new uuids to get rid of conflicts
	 * @returns serialized node state
	 */
	async copyNode(node, isNeedTonewUUids = true) {
		const userTemplateParams = await node.serializeNodeState(true, false);
		userTemplateParams.outputSocketGroup = [];

		if (node.definition.isNeedToCopyOutputs) {
			for (const socket of node.outputSocketGroup.getSockets()) {
				userTemplateParams.outputSocketGroup.push(
					new SocketDefinition(
						socket.name,
						socket.dataType,
						{
							...socket.optionalInfo,
							value: socket.value
						},
						socket.optional,
						socket?.displayName || socket.definition?.displayName
					)
				);
			}
		}
		if (isNeedTonewUUids) {
			userTemplateParams.uuid = generate_UUID();
			userTemplateParams.position.x -= 50;
			userTemplateParams.position.y -= 50;
		}
		return userTemplateParams;
	}

	/**
	 *
	 * @param {*} nodes
	 * @param {*} isNeedToGenNewUUids -
	 * @param {*} isNeedToPassOutputCons
	 */
	async serializeNodesAndThierConnections(
		nodes,
		isNeedToGenNewUUids = true,
		isNeedToPassOutputCons = true
	) {
		/**
		 * @type {IUserTemplateParams[]}
		 */
		const copiedNodesDefinitions = [];

		const copiedUUidsMap = {};
		for (const node of nodes) {
			const copiedNode = await this.copyNode(node, isNeedToGenNewUUids);
			copiedNodesDefinitions.push(copiedNode);
			copiedUUidsMap[node.uuid] = copiedNode.uuid;
		}
		// we alsways need to enter the loop for preventing overriding old connections with connections from copied nodes

		let nodeIndex = 0;

		// rewrite connection uuids to new generated uuids
		for (const node of nodes) {
			copiedNodesDefinitions[nodeIndex].connections = [];
			for (const outSocket of node.getAllSocketsOut()) {
				for (const connection of outSocket.connections) {
					const fromConnectionId = connection.socketOut.node.uuid;
					const toConnectionId = connection.socketIn.node.uuid;
					if (isNeedToPassOutputCons && !(toConnectionId in copiedUUidsMap)) continue;

					const fromUuid =
						fromConnectionId in copiedUUidsMap
							? copiedUUidsMap[fromConnectionId]
							: fromConnectionId;
					const toUuid = copiedUUidsMap[toConnectionId] || toConnectionId;
					copiedNodesDefinitions[nodeIndex].connections.push({
						from: {
							name: connection.socketOut.name,
							nodeUuid: fromUuid
						},
						to: {
							name: connection.socketIn.name,
							nodeUuid: toUuid
						}
					});
				}
			}
			for (const inSocket of node.getSocketsIn()) {
				for (const connection of inSocket.connections) {
					const fromConnectionId = connection.socketOut.node.uuid;

					if (fromConnectionId in copiedUUidsMap) continue;

					const toConnectionId = connection.socketIn.node.uuid;

					const fromUuid = fromConnectionId;
					const toUuid = copiedUUidsMap[toConnectionId];

					copiedNodesDefinitions[nodeIndex].connections.push({
						from: {
							name: connection.socketOut.name,
							nodeUuid: fromUuid
						},
						to: {
							name: connection.socketIn.name,
							nodeUuid: toUuid
						}
					});
				}
			}
			nodeIndex++;
		}
		return copiedNodesDefinitions;
	}

	async copySelectedNodes() {
		this.copiedNodesDefinitions = await this.serializeNodesAndThierConnections(
			this.selectedNodes
		);

		// Get selected comment frames
		const selectedCommentFrames = [];
		for (const frame of this.selectedCommentFrameMap.values()) {
			selectedCommentFrames.push(frame.serializeCommentFrame());
		}
		this.copiedCommentFrames = selectedCommentFrames;
		return {
			commentFrames: selectedCommentFrames,
			nodes: this.copiedNodesDefinitions
		};
	}

	/**
	 * used for restoring graph state on SPA navigation
	 * @param {boolean} isNeedToGenNewUUids - used to generate new UUIDs for nodes and connections
	 * @param {boolean} isNeedToPassOutputCons - used to pass output connections
	 * @returns serialized state of the graph, without navigation state
	 */
	async serializeState(isNeedToGenNewUUids = true, isNeedToPassOutputCons = true) {
		const nodes = await this.serializeNodesAndThierConnections(
			this.nodes,
			isNeedToGenNewUUids,
			isNeedToPassOutputCons
		);

		// Get selected comment frames
		const selectedCommentFrames = [];
		for (const frame of this.commentFrameMap.values()) {
			selectedCommentFrames.push(frame.serializeCommentFrame());
		}
		return {
			commentFrames: selectedCommentFrames,
			nodes: nodes
		};
	}

	executeLeavsNodes() {
		let nodes;
		if (this.selectedNodes.length > 0) {
			nodes = this.foundLeavesNodes(this.selectedNodes, this.selectedNodesMap);
		} else {
			nodes = this.foundLeavesNodes(this.nodes);
		}
		for (const node of nodes) {
			node.execute?.();
		}
	}

	/**
	 *
	 * @param {GraphNode[]} firstGeneration
	 * @returns {GraphNode[]}
	 */
	findAllOutConnectedNodes(firstGeneration) {
		const connectedNodes = [];
		const connectedMap = {};
		const stack = [];

		for (const firstNode of firstGeneration) {
			const nodes = firstNode.getAllOutConnectedNodes();
			connectedMap[firstNode.uuid] = true;
			for (const conenctedNode of nodes) {
				stack.push(conenctedNode);
			}
		}
		while (stack.length) {
			const node = stack.pop();
			if (node.uuid in connectedMap) continue;
			connectedNodes.push(node);
			connectedMap[node.uuid] = true;
			for (const nextGenNode of node.getAllOutConnectedNodes()) {
				if (nextGenNode.uuid in connectedMap) continue;
				stack.push(nextGenNode);
			}
		}

		return connectedNodes;
	}
	/**
	 *
	 * @param {GraphNode[]} firstGeneration
	 * @returns {GraphNode[]}
	 */
	findAlltConnectedNodes(firstGeneration) {
		const connectedNodes = [];
		const connectedMap = {};
		const stack = [];

		for (const firstNode of firstGeneration) {
			const nodes = firstNode.getAllConnectedNodes();
			connectedMap[firstNode.uuid] = true;
			for (const conenctedNode of nodes) {
				stack.push(conenctedNode);
			}
		}
		while (stack.length) {
			const node = stack.pop();
			if (node.uuid in connectedMap) continue;
			connectedNodes.push(node);
			connectedMap[node.uuid] = true;
			for (const nextGenNode of node.getAllConnectedNodes()) {
				if (nextGenNode.uuid in connectedMap) continue;
				stack.push(nextGenNode);
			}
		}

		return connectedNodes;
	}
	/**
	 * @description return leaves nodes founded in selection or in all nodes
	 * @param nodesSelection {GraphNode[]}
	 * @param selectionMap {Map<string, GraphNode>|undefined}
	 * @returns {GraphNode[]}
	 */
	foundLeavesNodes(nodesSelection, selectionMap) {
		const leavs = [];

		if (selectionMap) {
			mainLoop: for (const node of nodesSelection) {
				for (const outSocket of node.getSocketsOut()) {
					for (const connection of outSocket.connections) {
						if (selectionMap.has(connection.socketIn.node.uuid)) continue mainLoop;
					}
				}
				leavs.push(node);
			}
		} else {
			mainLoop: for (const node of nodesSelection) {
				for (const outSocket of node.getSocketsOut()) {
					if (outSocket.connections.length > 0) {
						continue mainLoop;
					}
				}
				leavs.push(node);
			}
		}

		return leavs;
	}

	foundAllRootNodes() {
		const roots = [];

		mainLoop: for (const node of this.nodes) {
			for (const inSockets of node.getSocketsIn()) {
				if (inSockets.connections.length > 0) {
					continue mainLoop;
				}
			}
			roots.push(node);
		}
		return roots;
	}

	/**
	 *
	 * @param nodesInSelection {GraphNode[]}
	 * @param selectionMap {Map<string, boolean>}
	 * @returns {GraphNode[]}
	 */
	foundAllRootNodesInSelection(nodesInSelection, selectionMap) {
		const roots = [];

		mainLoop: for (const node of nodesInSelection) {
			for (const inSockets of node.getSocketsIn()) {
				for (const connection of inSockets.connections) {
					if (selectionMap.has(connection.socketOut.node.uuid)) continue mainLoop;
				}
			}
			roots.push(node);
		}
		return roots;
	}
	enableRenderingOptimizaion() {
		if (this.isRenderingOptimizationaEnabled) return;
		this.isRenderingOptimizationaEnabled = true;
		this.dom.graph.style.willChange = "transform";
	}
	disableRenderingOptimization() {
		this.isRenderingOptimizationaEnabled = false;
		this.dom.graph.style.willChange = "auto";
	}
	initOptimizationListner() {
		const _listner = () => {
			const length = this.nodes.length;
			if (length > 40) {
				this.enableRenderingOptimizaion();
				return;
			}
			if (this.isRenderingOptimizationaEnabled) {
				this.disableRenderingOptimization();
			}
		};
	}
	/**
	 * @param curNodesGeneration {GraphNode[]} in first recursion step accept tupple of one root node, then goes through all connectet nodes in depth
	 * @param rootNodeXPos {number} accepts what the x position should be for the current recursion level
	 * @param rootNodeYPos {number} accepts what the x position should be for the current node level
	 * @param maxXPos {number} accepts accepts the current maximum found position x for the current node island,
	 * used to position the next node island to the right of the current one
	 * @param visitedNodesMap {Record<string, boolean>} the map of the visited nodes is used in order not to bypass the island several times if it has many roots
	 * @param parentHeightDelta {number}
	 * @description [see traversal techniques]{@link https://www.geeksforgeeks.org/tree-traversals-inorder-preorder-and-postorder/}
	 */
	recursionAlignByNodeGeneration(
		curNodesGeneration,
		rootNodeXPos,
		rootNodeYPos,
		maxXPos,
		visitedNodesMap,
		parentHeightDelta = null
	) {
		let returnYDelta = 0;
		let i = 0;
		const foundedHeight = curNodesGeneration[0].layout.getHeight() + 50;

		if (foundedHeight !== parentHeightDelta) {
			returnYDelta += foundedHeight - parentHeightDelta;
		}
		for (const node of curNodesGeneration) {
			if (node.uuid in visitedNodesMap) continue;
			visitedNodesMap[node.uuid] = true;

			const _curNodePos = node.layout.getPosition();
			const connectedNodes = node.getAllOutConnectedNodes();

			node.layout.setPosition(rootNodeXPos, rootNodeYPos, true);
			const foundedHeight = node.layout.getHeight() + 100;
			const nodeWidth = node.layout.getWidth();
			const nextNodePos = nodeWidth + rootNodeXPos + 100;

			if (maxXPos < nextNodePos) {
				maxXPos = nextNodePos;
			}

			if (connectedNodes.length > 0) {
				const { returnYDelta: foundedDelta, maxXPos: maxXReturnedPos } =
					this.recursionAlignByNodeGeneration(
						connectedNodes,
						nextNodePos,
						rootNodeYPos,
						maxXPos,
						visitedNodesMap,
						foundedHeight
					);
				if (foundedDelta > foundedHeight - parentHeightDelta) {
					returnYDelta += foundedDelta;
					rootNodeYPos += foundedDelta;
				}
				if (maxXReturnedPos > maxXPos) {
					maxXPos = maxXReturnedPos;
				}
			}

			rootNodeYPos += foundedHeight;

			if (i > 0) {
				returnYDelta += foundedHeight;
			}

			i++;
		}
		return { returnYDelta, maxXPos };
	}

	alingAllNodesByRoots() {
		let roots;
		if (this.selectedNodes.length > 0) {
			roots = this.foundAllRootNodesInSelection(this.selectedNodes, this.selectedNodesMap);
		} else {
			roots = this.foundAllRootNodes();
		}
		if (roots.length < 1) return;

		const firstPos = roots[0].layout.getPosition();
		let newXPos = firstPos.x;
		const visitedNodesMap = {};

		for (const root of roots) {
			const { maxXPos } = this.recursionAlignByNodeGeneration(
				[root],
				newXPos,
				firstPos.y,
				newXPos,
				visitedNodesMap
			);
			newXPos += maxXPos - newXPos + 100;
		}
	}

	/**
	 *
	 * @param templateParams {IUserTemplateParams}
	 * @param undoManagerIgnored
	 * @param copiedOutputs
	 * @returns {Promise<GraphNode>}
	 */
	async pasteNodeByGlobalidAndParams(
		templateParams,
		undoManagerIgnored = false,
		copiedOutputs = true,
		isModificationEnabled = true
	) {
		const definitionToPaste = await getDefinitionOrAwait(templateParams.globalId);
		const definition = Object.assign({}, definitionToPaste);
		if (copiedOutputs) {
			definition.copiedOutputs = templateParams.outputSocketGroup;
			definition.isNeedToInitViewer =
				templateParams.outputSocketGroup.length > 0 &&
				!!templateParams.outputSocketGroup[0]?.optionalInfo?.value;
		}
		definition.userTemplateParams = templateParams;
		definition.isModificationEnabled = isModificationEnabled;
		return this.addNode(definition, 0, 0, false, false, undoManagerIgnored);
	}

	/**
	 *
	 * @param templateHistoryParams {HistoryResponseListItem}
	 * @param nodeUuid
	 * @param onNodeCreated {(node: GraphNode) => void}
	 * @returns {Promise<GraphNode|undefined>}
	 */
	async pasteImportFilesFromNodeInputs(templateHistoryParams, nodeUuid, onNodeCreated) {
		const originalNode = this.nodesHashMap.get(nodeUuid);
		if (!originalNode) return;
		const position = originalNode.layout.getPosition();
		const userTemplateParam = {
			inputSocketgroup: [],
			outputSocketgroup: [],
			position: {
				x: position.x - originalNode.layout.getWidth() - 50,
				y: position.y
			}
		};

		const outputSocketGroup = [];

		let isNeedToPaste = false;
		const inputArr = Array.isArray(templateHistoryParams.params.inputs)
			? templateHistoryParams.params.inputs
			: Object.values(templateHistoryParams.params.inputs);
		for (const input of inputArr) {
			// Skip array inputs with no files (value can be null from feed/backend for optional inputs)
			if (input.type === "array" && (!input.value || !input.value.length)) {
				continue;
			}
			if (input.type !== "text" && input.value) {
				// is field duplicate backend field _link prefix holds links instead of fileId
				if (typeof input.value !== "number" && !Array.isArray(input.value)) {
					continue;
				}
				isNeedToPaste = true;
				outputSocketGroup.push({
					name: input.name,
					value: Array.isArray(input.value)
						? input.value.map((item) => ({ fileId: item }))
						: [{ fileId: input.value }],
					type: input.type,
					toName: input.name
				});
			}
		}
		if (!isNeedToPaste) return;

		const importFiles = Object.assign({}, await getDefinitionOrAwait("Import Files"));
		importFiles.useOutputViewer = true;

		importFiles.userTemplateParams = userTemplateParam;
		const importFilesNode = this.addNode(importFiles, 0, 0, false, false, true);
		onNodeCreated(importFilesNode);
		importFilesNode.loader.show();

		// Collect all fileIds into one combined socket and pass to updateNodeWithFiles
		// This reuses existing file loading logic from workspace.ts
		const allFileIds = [];
		for (const outputSocket of outputSocketGroup) {
			if (Array.isArray(outputSocket.value)) {
				allFileIds.push(...outputSocket.value);
			}
		}

		// Create a combined socket with all files for Import Files node
		const combinedSocket = {
			name: "combined",
			value: allFileIds,
			type: "image",
			optionalInfo: outputSocketGroup[0]?.optionalInfo
		};

		await updateNodeWithFiles(importFilesNode, combinedSocket);

		for (const outSocket of outputSocketGroup) {
			const toNodeSocket = originalNode.inputSocketGroup.socketHashMap.get(outSocket.toName);
			if (!toNodeSocket) continue;
			const socketsFrom = importFilesNode
				.getAllSocketsOut()
				.filter((socket) =>
					Array.isArray(outSocket.value)
						? !!outSocket.value.find(
								(out) => out.fileId === socket.tryGetValue()?.fileId
							)
						: socket.value === outSocket.value
				);
			for (const socket of socketsFrom) {
				new Connection(toNodeSocket, socket, true, true, null, true);
			}
		}
		importFilesNode.loader.hide();

		return importFilesNode;
	}

	fitWorkspaceToNode(uuid) {
		const node = this.nodesHashMap.get(uuid);
		if (!node) return;
		fitEditorToBoundary(calculateBoundariesOfNode(node));
	}

	pasteConnectionsFromTemplateParams(templateParams) {
		for (const connection of templateParams.connections) {
			const fromGraphNode = this.nodesHashMap.get(connection.from.nodeUuid);
			const toGraphNode = this.nodesHashMap.get(connection.to.nodeUuid);

			if (fromGraphNode && toGraphNode) {
				const fromNodeSocket = fromGraphNode.outputSocketGroup.socketHashMap.get(
					connection.from.name
				);
				const toNodeSocket = toGraphNode.inputSocketGroup.socketHashMap.get(
					connection.to.name
				);
				if (fromNodeSocket && toNodeSocket) {
					new Connection(toNodeSocket, fromNodeSocket, true, true, null, true);
				}
			}
		}
	}

	async pasteNodes(
		nodesUserTemplateParamAndGlobalId,
		undoManagerIgnored = false,
		copiedOutputs = true,
		isNeedToGenNewUUids = true
	) {
		if (isNeedToGenNewUUids) {
			nodesUserTemplateParamAndGlobalId = this.updateUUIDsInCopiedDefinition(
				nodesUserTemplateParamAndGlobalId
			);
		}
		const pastedNodes = await Promise.all(
			nodesUserTemplateParamAndGlobalId.map((definition) => {
				return this.pasteNodeByGlobalidAndParams(
					definition,
					undoManagerIgnored,
					copiedOutputs,
					false
				);
			})
		);

		for (const templateParams of nodesUserTemplateParamAndGlobalId) {
			this.pasteConnectionsFromTemplateParams(templateParams);
		}
		for (const node of pastedNodes) {
			node.setIsModificationEnabled(true);
		}
		this.selectNodeList(pastedNodes);
		return pastedNodes;
	}

	updateUUIDsInCopiedDefinition(nodesUserTemplateParamAndGlobalId) {
		const uuidsMap = {};
		const newTemplateParams = [];
		for (const templateParams of nodesUserTemplateParamAndGlobalId) {
			const _templateParams = Object.assign({}, templateParams);
			const newUUID = generate_UUID();

			uuidsMap[_templateParams.uuid] = newUUID;
			_templateParams.uuid = newUUID;

			newTemplateParams.push(_templateParams);
		}

		for (const templateParams of newTemplateParams) {
			if (!templateParams.connections) continue;
			for (const connection of templateParams.connections) {
				if (connection.from.nodeUuid in uuidsMap) {
					connection.from.nodeUuid = uuidsMap[connection.from.nodeUuid];
				}
				if (connection.to.nodeUuid in uuidsMap) {
					connection.to.nodeUuid = uuidsMap[connection.to.nodeUuid];
				}
			}
		}

		return newTemplateParams;
	}

	pasteCopiedNodes() {
		this.deselectAllNodes();
		this.pasteNodes(this.copiedNodesDefinitions);
	}

	async deleteSelectedNodes() {
		const nodes = [...this.selectedNodes];
		this.undoManager.deleteNodeFromManager(
			await this.serializeNodesAndThierConnections(nodes, false, false)
		);
		for (const node of nodes) {
			this.deleteNode(node, false, true);
		}
		this.selectedNodesMap.clear();
	}
	deleteSelectedCommentFrames() {
		for (const id of this.selectedCommentFrameMap.keys()) {
			this.deleteCommentFrame({ id });
		}
	}

	removeNodeFromSelected(node) {
		const nodeIndex = this.selectedNodes.indexOf(node);
		if (nodeIndex === -1) return;

		this.selectedNodes.splice(nodeIndex, 1);
		node.setSelect(false);
		this.selectedNodesMap.delete(node.uuid);
	}

	deselectAllNodes() {
		for (const selectedNode of this.selectedNodes) {
			selectedNode.setSelect(false);
		}
		this.selectedNodes = [];
		this.selectedNodesMap.clear();
	}

	singleSelectNode(node) {
		this.deselectAllCommetFrames();
		this.deselectAllNodes();
		this.addNodeToSelected(node);
	}

	/**
	 * Handles node selection based on mouse event (shift key state)
	 * @param {GraphNode} node - The node to select
	 * @param {MouseEvent} event - The mouse event (used to check shift key)
	 */
	handleNodeSelection(node, event) {
		if (event.shiftKey) {
			if (this.selectedNodes.includes(node)) {
				this.removeNodeFromSelected(node);
			} else {
				this.addNodeToSelected(node);
			}
		} else {
			this.singleSelectNode(node);
		}
	}

	selectNodeList(nodes) {
		this.deselectAllCommetFrames();
		this.deselectAllNodes();
		for (const node of nodes) {
			this.addNodeToSelected(node);
		}
	}

	/**
	 *found all connected nodes for passed nodes and init listners for mvement chortcut
	 * @param {GraphNode[]} nodes
	 * @param {number} startX
	 * @param {number} startY
	 */
	initMoveAllConnectedNodes(nodes, startX, startY) {
		const connectedNodes = this.findAllOutConnectedNodes(nodes);
		let resolveFc;
		const pos = {
			x: startX,
			y: startY
		};
		const keyDownHandler = (e) => {
			if (e.code !== "ShiftLeft") return;
			const cancelPromise = new Promise((resolve) => {
				resolveFc = resolve;
			});

			this.moveNodeList(
				connectedNodes,
				pos.x,
				pos.y,
				(node) => this.postMovmentCallback(node),
				cancelPromise,
				true
			);
		};
		const keyUpHandler = (_e) => {
			resolveFc?.();
		};

		const mouseMoveHandler = (e) => {
			pos.x = e.clientX;
			pos.y = e.clientY;
		};
		const mouseUpHander = (_e) => {
			keyUpHandler();
			document.removeEventListener("keyup", keyUpHandler);
			document.removeEventListener("keydown", keyDownHandler);
			document.removeEventListener("mouseup", mouseUpHander);
			document.removeEventListener("mousemove", mouseMoveHandler);
		};
		document.addEventListener("keydown", keyDownHandler);
		document.addEventListener("keyup", keyUpHandler);
		document.addEventListener("mouseup", mouseUpHander);
		document.addEventListener("mousemove", mouseMoveHandler);
	}

	moveAllSelectedNodes(mouseEvent) {
		if (mouseEvent.which !== 1) return;
		if (!mouseEvent.altKey) {
			for (const commentFrame of this.selectedCommentFrameMap.values()) {
				commentFrame.moveSticker(mouseEvent.clientX, mouseEvent.clientY);
			}
		}

		this.moveNodeList(this.selectedNodes, mouseEvent.clientX, mouseEvent.clientY, (node) => {
			this.postMovmentCallback(node);
		});
		setTimeout(() =>
			this.initMoveAllConnectedNodes(
				this.selectedNodes,
				mouseEvent.clientX,
				mouseEvent.clientY
			)
		);
	}

	changeRelationNodeToCommentFrame(node, newId) {
		if (node.relatedCommentId !== undefined) {
			const oldComment = this.commentFrameMap.get(node.relatedCommentId);
			if (oldComment) {
				oldComment.removeFromNodeToCommentFrame(node.uuid);
			}
		}
		node.setRelatedToComment(newId);
	}

	isNodeRelatedToCommentFrame(node, commentFrame) {
		const isFeet = node.layout.isFitToBoundary(commentFrame.boundary);
		if (isFeet) {
			if (commentFrame.id === node.relatedCommentId) {
				return true;
			}
			this.changeRelationNodeToCommentFrame(node, commentFrame.id);
			commentFrame.addNodeToCommentFrame(node.uuid);
			return true;
		}
		if (commentFrame.id === node.relatedCommentId) {
			this.changeRelationNodeToCommentFrame(node, undefined);
		}
		return false;
	}

	checkIsNodeIsRelatedToCommentFrames(node) {
		let isRelated = false;
		for (const [, commentFrame] of this.commentFrameMap) {
			isRelated = this.isNodeRelatedToCommentFrame(node, commentFrame);
			if (isRelated) {
				break;
			}
		}
	}

	postMovmentCallback(node) {
		this.checkIsNodeIsRelatedToCommentFrames(node);
		this.updateUndoManagerNodePosition(node);
		node.isMoving = false;
	}

	updateUndoManagerNodePosition(node) {
		this.undoManager.updateNodePosition(node.uuid, node.layout.getPosition());
	}

	/**
	 *
	 * @param {GraphNode} node
	 * @param {number} startX
	 * @param {number} startY
	 * @param {(node: GraphNode)=>void=} endMovementCallback fires on end of movemend
	 * @param {Promise=} cancelMovementPromise optional promise for canceling movement
	 */
	moveNode(node, startX, startY, endMovementCallback, cancelMovementPromise) {
		if (node.isMoving) return;
		node.isMoving = true;
		const zoomScale = this.navigation.getZoomScale();
		const nodePos = node.layout.getPosition();
		const x = nodePos.x - startX / zoomScale;
		const y = nodePos.y - startY / zoomScale;

		const connections = [];

		const socketsIn = node.getSocketsIn();

		for (const socket of socketsIn) {
			for (const connection of socket.connections) {
				if (connection.getIsInRedrawList()) continue;
				connection.toggleAddedToRedrawList(true);
				connections.push(connection);
			}
		}
		const socketsOut = node.getAllSocketsOut();
		for (const socket of socketsOut) {
			for (const connection of socket.connections) {
				if (connection.getIsInRedrawList()) continue;
				connection.toggleAddedToRedrawList(true);
				connections.push(connection);
			}
		}
		if (this.nodes[this.nodes.length - 1] === node) {
			this.isLastNodeWasMoved = true;
		}
		const mouseMoveHandler = (e) => {
			this.nodesMovedExecutor = true;
			this.nodesMoved = true;
			node.layout.setPosition(e.clientX / zoomScale + x, e.clientY / zoomScale + y);

			for (const connection of connections) {
				connection.redrawConnection();
			}
		};

		const mouseUpHandler = () => {
			this.toggleIsSavedState(false);
			for (const connection of connections) {
				connection.toggleAddedToRedrawList(false);
			}
			node.isMoving = false;
			endMovementCallback?.(node);
			document.removeEventListener("mousemove", mouseMoveHandler);
			document.removeEventListener("mouseup", mouseUpHandler);
		};
		cancelMovementPromise?.then(() => mouseUpHandler());

		document.addEventListener("mousemove", mouseMoveHandler);
		document.addEventListener("mouseup", mouseUpHandler);
	}
	/**
	 *
	 * @param {GraphNode[]} nodes
	 * @param {number} startX The initial mouse clientX coordinate
	 * @param {number} startY The initial mouse clientY coordinate
	 * @param {(node: GraphNode)=>void} endMovementCallback fires on end of movement
	 * @param {Promise} cancelMovementPromise optional promise for canceling movement
	 * @param {boolean} isNeedToCheckNotRedrawed used to determine if need to check all connections after movement
	 */
	moveNodeList(
		nodes,
		startX,
		startY,
		endMovementCallback,
		cancelMovementPromise,
		isNeedToCheckNotRedrawed = false
	) {
		const zoomScale = this.navigation.getZoomScale();

		const connections = [];
		const notRedrawedConnections = [];

		// --- State for Alt dragging ---
		let isAltPressed = false;
		let lastMouseMoveEvent;
		const nodeMovementList = nodes
			.filter((node) => !node.isMoving)
			.map((node) => {
				node.isMoving = true; // Set early, reset on mouseup/cancel
				const nodePos = node.layout.getPosition();

				const originalX = nodePos.x;
				const originalY = nodePos.y;
				const offsetX = nodePos.x - startX / zoomScale;
				const offsetY = nodePos.y - startY / zoomScale;

				for (const socket of node.getAllSockets()) {
					for (const connection of socket.connections) {
						// Connection handling remains the same
						if (connection.getIsInRedrawList()) {
							connection.setIsDoubleRedraw(true);
							if (!isNeedToCheckNotRedrawed) continue;
							notRedrawedConnections.push(connection);
							continue;
						}
						connection.toggleAddedToRedrawList(true);
						connections.push(connection);
					}
				}
				const ghost = node.createGhost();
				ghost.style.display = "none";
				this.dom.graph.appendChild(ghost);
				return {
					node,
					offsetX,
					offsetY,
					originalX,
					originalY,
					ghost: ghost
				};
			});

		if (nodeMovementList.length === 0) {
			console.warn("moveNodeList called with no movable nodes.");
			return; // Nothing to do
		}

		const removeAllGhosts = () => {
			for (const moveElement of nodeMovementList) {
				moveElement.ghost.remove();
			}
		};
		const updateVisualPositions = () => {
			if (!lastMouseMoveEvent) return; // Don't update if inactive or no mouse data

			const currentMouseX = lastMouseMoveEvent.clientX / zoomScale;
			const currentMouseY = lastMouseMoveEvent.clientY / zoomScale;

			for (const item of nodeMovementList) {
				const targetPos = {
					x: currentMouseX + item.offsetX,
					y: currentMouseY + item.offsetY
				};

				if (isAltPressed) {
					// Alt is pressed: Show ghost at target, move node to original
					item.node.layout.setPosition(item.originalX, item.originalY);
					item.ghost.style.transform = `translate(${targetPos.x}px, ${targetPos.y}px)`;
					item.ghost.style.display = "block";
				} else {
					// Alt is not pressed: Move node to target, hide ghost
					item.node.layout.setPosition(targetPos.x, targetPos.y);
					item.ghost.style.display = "none";
				}
			}
			for (const connection of connections) {
				connection.redrawConnection();
			}
		};

		const keyDownHandler = (e) => {
			if (e.key !== "Alt" || isAltPressed) {
				// Only react to Alt keydown, and only if it wasn't already pressed
				return;
			}
			isAltPressed = true;
			e.preventDefault(); // Prevent browser Alt menu if needed
			updateVisualPositions(); // Instantly update visuals
		};

		const keyUpHandler = (e) => {
			if (e.key !== "Alt" || !isAltPressed) {
				// Only react to Alt keyup, and only if it was pressed
				return;
			}
			isAltPressed = false;
			updateVisualPositions(); // Instantly update visuals
		};

		// --- Event Handlers ---

		const mouseMoveHandler = (e) => {
			lastMouseMoveEvent = e;
			this.nodesMoved = true; // Flag that movement occurred
			isAltPressed = e.altKey;
			updateVisualPositions();
		};

		const mouseUpHandler = (e) => {
			// --- Cleanup Logic ---
			document.removeEventListener("mousemove", mouseMoveHandler);
			document.removeEventListener("mouseup", mouseUpHandler);
			document.removeEventListener("keydown", keyDownHandler);
			document.removeEventListener("keyup", keyUpHandler);

			// ALWAYS remove any remaining ghosts on mouse up
			removeAllGhosts();

			if (e?.altKey) {
				this.serializeNodesAndThierConnections(
					nodeMovementList.map((item) => item.node),
					false,
					true
				).then((copiedNodes) => {
					for (const node of copiedNodes) {
						const originalNode = nodeMovementList.find(
							(n) => n.node.uuid === node.uuid
						);
						if (!lastMouseMoveEvent) continue;
						const curPosX = lastMouseMoveEvent.clientX / zoomScale;
						const curPosY = lastMouseMoveEvent.clientY / zoomScale;
						node.position.x = originalNode.offsetX + curPosX;
						node.position.y = originalNode.offsetY + curPosY;
					}

					this.pasteNodes(copiedNodes);
				});
			}
			const finalX = e ? e.clientX : startX; // Use start if no event (cancel)
			const finalY = e ? e.clientY : startY;
			const xDiff = finalX - startX;
			const yDiff = finalY - startY;
			// Check if user moved node significantly
			if (Math.max(Math.abs(xDiff), Math.abs(yDiff)) > 10) {
				this.nodesMovedExecutor = true;
				this.toggleIsSavedState(false); // Mark state as unsaved
			}
			setTimeout(() => {
				this.nodesMovedExecutor = false;
			});

			// Reset connection redraw flags
			for (const connection of connections) {
				connection.setIsDoubleRedraw(false);
				connection.toggleAddedToRedrawList(false);
			}
			for (const connection of notRedrawedConnections) {
				connection.setIsDoubleRedraw(false);
			}

			// Reset node moving state and call callback
			// Node positions are left as they were upon the last mouseMove/Alt state check
			for (const nodeMovementItem of nodeMovementList) {
				nodeMovementItem.node.isMoving = false; // Reset moving flag
				endMovementCallback?.(nodeMovementItem.node);
			}
			// --- End Cleanup ---
		};

		// Handle external cancellation
		cancelMovementPromise?.then(() => mouseUpHandler(null)); // Pass null for event

		// Add initial listeners
		document.addEventListener("mousemove", mouseMoveHandler);
		document.addEventListener("mouseup", mouseUpHandler);
		document.addEventListener("keydown", keyDownHandler);
		document.addEventListener("keyup", keyUpHandler);
	}

	async handlePasteEvent(event) {
		event.preventDefault();
		readClipboardData(event.clipboardData);
		const files = handlePasteImageEvent(event);
		if (!files || !files.length) {
			return;
		}
		addLocalNodeByGlobalId({
			globalId: "Import Files",
			newOutputsFiles: files
		});
	}

	toggleFocus(force) {
		this.isFocused = force;
		if (isDebugFlag(DebugFlags.GraphFocusDebug)) {
			this.dom.editorSpace.classList.toggle(twJoin("!bg-red-200"), force);
		}
	}

	initNodeActions() {
		// Paste event handler
		this.eventHandlers.paste = (e) => {
			if (
				!this.isFocused ||
				e.target.nodeName === "INPUT" ||
				e.target.nodeName === "TEXTAREA"
			) {
				return;
			}
			this.handlePasteEvent(e);
		};
		document.addEventListener("paste", this.eventHandlers.paste);

		// Copy event handler
		this.eventHandlers.copy = (e) => {
			if (
				!this.isFocused ||
				e.target.nodeName === "INPUT" ||
				e.target.nodeName === "TEXTAREA"
			) {
				return;
			}
			console.log("copy");
			this.copySelectedNodes().then(() =>
				copiedNodesToClipBoardFormats(this.copiedNodesDefinitions, this.copiedCommentFrames)
			);
		};
		document.addEventListener("copy", this.eventHandlers.copy);

		// Editor space pointer down (focus)
		this.eventHandlers.pointerDown = (e) => {
			e.stopPropagation();
			this.toggleFocus(true);
		};
		this.dom.editorSpace.addEventListener("pointerdown", this.eventHandlers.pointerDown);

		// Body pointer down (blur)
		this.eventHandlers.bodyPointerDown = () => {
			this.toggleFocus(false);
		};
		document.body.addEventListener("pointerdown", this.eventHandlers.bodyPointerDown);

		// Global keydown handler
		this.eventHandlers.keyDown = (event) => {
			if (
				!this.isFocused ||
				event.repeat ||
				event.target.nodeName === "INPUT" ||
				event.target.nodeName === "TEXTAREA"
			)
				return;
			const _code = event.code;

			if (event.ctrlKey || event.metaKey) {
				if (event.code === "Enter") {
					this.executeLeavsNodes();
					return;
				}
				if (event.code === "KeyD") {
					event.preventDefault();
					this.copySelectedNodes().then(() => {
						updateNodePositionsForCentering({
							data: this.copiedNodesDefinitions,
							commentFrames: this.copiedCommentFrames,
							version: 0
						});
						this.pasteCopiedNodes();
						for (const item of this.copiedCommentFrames) {
							loadCommentFrameAccordType(item);
						}
					});
					return;
				}
				if (event.code === "KeyA") {
					event.preventDefault();
					this.takeActionOnAllNodes((node) => this.addNodeToSelected(node));
					for (const commentFrame of this.commentFrameMap.values()) {
						this.addCommentFrameToSelect(commentFrame);
					}
					return;
				}
				if (event.code === "KeyZ" && event.shiftKey) {
					event.preventDefault();
					this.undoManager.redo();
					return;
				}
				if (event.code === "KeyZ") {
					event.preventDefault();
					this.undoManager.undo();
					return;
				}
			}
			if (event.key === "Backspace" || event.key === "Delete") {
				event.stopPropagation();
				event.preventDefault();
				this.deleteSelectedNodes();
				this.deleteSelectedCommentFrames();
			}
		};
		document.addEventListener("keydown", this.eventHandlers.keyDown);
	}

	/**@param {(Node) => {}} action*/
	takeActionOnSelectedNodes(action) {
		for (let i = 0; i < this.selectedNodes.length; i++) {
			action(this.selectedNodes[i]);
		}
	}
	/**@param {(Node) => {}} action*/
	takeActionOnAllNodes(action) {
		for (let i = 0; i < this.nodes.length; i++) {
			action(this.nodes[i]);
		}
	}

	enableStartTips() {
		// document.querySelector('#start-tips').classList.remove('hidden')
	}

	disableStartTips() {
		// document.querySelector('#start-tips').classList.add('hidden')
	}
}
